import { Todo } from '@/common/types/databaseTypes';
import { useCategoryStore } from '@/stores/categoryStore';
import { Box, Button, Group, Menu, Tooltip } from '@mantine/core';
import { createStyles } from '@mantine/styles';
import { IconChevronDown } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

interface CategoryMenuProps {
    todo?: Todo;
    categoryId?: string;
    onChangeCategory: (categoryId: string | null, todo?: Todo) => void;
    tabIndex?: number;
    withinPortal?: boolean;
}

const useStyles = createStyles(theme => {
    return {
        categoryMenuItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '.5rem',
            color: 'black',
        },
        colorChip: {
            width: '1.25rem',
            height: '1.25rem',
            borderRadius: '50%',
            flexShrink: 0,
        },
    };
});

export const CategoryMenu = ({
    todo,
    categoryId,
    onChangeCategory,
    tabIndex,
    withinPortal = true,
}: CategoryMenuProps) => {
    const { t } = useTranslation('todo');
    const { classes, theme } = useStyles();
    const { categories, isLoading } = useCategoryStore();
    const currentColor =
        categories?.find(category => category.id === categoryId)?.color ||
        // theme.colors.gray[3];
        categories?.[0]?.color ||
        '#e9ecef';

    return (
        <Menu
            withinPortal={withinPortal}
            radius="sm"
            onChange={opened => {
                const item = document.querySelector(
                    `[data-todo-id="${todo?.id}"]`
                );
                if (item) {
                    item.setAttribute('data-menu-opened', opened.toString());
                }
            }}
        >
            <Menu.Target>
                <Tooltip
                    label={t(
                        'todo:component.todoDrawer.category_menu.change_category'
                    )}
                    withArrow
                >
                    <Button
                        c="dimmed"
                        tabIndex={tabIndex}
                        variant="default"
                        size="compact-sm"
                        color="gray"
                        radius="xl"
                    >
                        <Group gap="4px">
                            <Box
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '100px',
                                    backgroundColor: currentColor,
                                }}
                            />
                            <IconChevronDown color="gray" size={14} />
                        </Group>
                    </Button>
                </Tooltip>
            </Menu.Target>

            <Menu.Dropdown>
                {isLoading ? (
                    <Menu.Item>
                        {t('todo:component.todoDrawer.category_menu.loading')}
                    </Menu.Item>
                ) : (
                    categories?.map(category => (
                        <Menu.Item
                            key={category.id}
                            bg={
                                category.id === categoryId
                                    ? 'gray.1'
                                    : undefined
                            }
                            onClick={e => {
                                e.stopPropagation();
                                onChangeCategory(category.id, todo);
                            }}
                        >
                            <div className={classes.categoryMenuItem}>
                                <span
                                    className={classes.colorChip}
                                    style={{
                                        backgroundColor: category.color,
                                    }}
                                ></span>
                                <span>
                                    {category.displayName || category.name}
                                </span>
                            </div>
                        </Menu.Item>
                    ))
                )}
            </Menu.Dropdown>
        </Menu>
    );
};
